import React, { useState } from 'react';
import { X } from 'lucide-react';
import { licensesAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface CreateLicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateLicenseModal: React.FC<CreateLicenseModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim() || !displayName.trim() || !expiresOn) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(expiresOn)) {
      toast.error('Please enter a valid date in YYYY-MM-DD format');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await licensesAPI.createLicense({
        code: code.trim().toLowerCase(),
        display_name: displayName.trim(),
        expires_on: expiresOn,
        description: description.trim() || null,
        is_active: true,
      });

      // If a file is selected, upload it for this license
      if (file && result.licenseId) {
        try {
          await licensesAPI.uploadLicenseFile(result.licenseId, file);
        } catch (uploadError: any) {
          console.error('Error uploading license file:', uploadError);
          toast.error(uploadError.response?.data?.error || 'License created, but file upload failed');
        }
      }

      toast.success('License created successfully!');
      onClose();
      onSuccess();
      // Reset form
      setCode('');
      setDisplayName('');
      setExpiresOn('');
      setDescription('');
      setFile(null);
    } catch (error: any) {
      console.error('Error creating license:', error);
      toast.error(error.response?.data?.error || 'Failed to create license');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">Create New License</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              License Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              placeholder="e.g., nonmem, matlab, stata"
              required
            />
            <p className="mt-1 text-xs text-gray-500">Lowercase letters, numbers, hyphens, and underscores only</p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              placeholder="e.g., NONMEM License"
              required
            />
          </div>

          {/* Expires On */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiration Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              rows={3}
              placeholder="Optional description of the license"
            />
          </div>

          {/* License File (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              License File (optional)
            </label>
            <input
              type="file"
              accept=".lic"
              onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
              className="w-full text-sm text-gray-700"
            />
            <p className="mt-1 text-xs text-gray-500">
              Upload the license file (e.g., nonmem-2026-2027.lic). This will be stored under the license-specific folder.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white font-medium hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create License'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLicenseModal;
